import {
  ChangeDetectorRef, Component, ElementRef, forwardRef, OnDestroy, OnInit, QueryList, ViewChild,
  ViewChildren
} from '@angular/core';
import {
  AbstractControl, ControlValueAccessor, FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, UntypedFormControl,
  UntypedFormGroup, Validator, Validators
} from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import {
  cloneDeep as _cloneDeep, forEach as _forEach, isEqual as _isEqual, uniq as _uniq
} from 'lodash-es';
import { startWith, takeUntil } from 'rxjs/operators';

import { DAYS } from 'core/date/days';
import { TIMES30 } from 'core/date/hours-mins';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { GetTimeZoneNames } from 'core/timezones/timezones2';
import { doTimeIntervalsOverlap, isNilOrEmpty } from 'helpers';
import { getTimeZones } from '@vvo/tzdb';

interface DayOfWeekItem {
  dayOfWeek: string;
  position: number;
  selected: boolean;
  times: StartEndTime[];
}

export interface StartEndTime {
  startTime?: string;
  endTime?: string;
}

export interface ShiftInfoFormModel {
  id?: string;
  timezone: string;
  shiftTimes: ShiftTimeInputModel[];
}

export interface ShiftTimeInputModel {
  dayOfWeek: string;
  startTime?: string | null;
  endTime?: string | null;
}
export interface TimeDropdownOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-shift-info-form',
  templateUrl: './shift-info-form.component.html',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => ShiftInfoFormComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => ShiftInfoFormComponent), multi: true }
  ]
})
export class ShiftInfoFormComponent extends WithDisposableSubject() implements
  OnDestroy, ControlValueAccessor, Validator, OnInit {

  @ViewChild('startTimeAutocomplete') startTimeAutocomplete: MatAutocomplete;
  @ViewChild('endTimeAutocomplete') endTimeAutocomplete: MatAutocomplete;
  // This is used to open the end time for next day shift after a user clicks "next day"
  // from the previous shift
  @ViewChildren('endTimeInput') endTimeInputs: QueryList<ElementRef>;

  disabled = false;
  dayList = DAYS;
  timezones = GetTimeZoneNames();

  timeList: {
    value: string;
    text: string;
  }[] = _cloneDeep(TIMES30);
  filteredTimeList: TimeDropdownOption[] = [];

  form = new FormGroup({
    shiftDays: new FormControl<string[]>([]),
    timezone: new FormControl<string>(null, {
      validators: Validators.required
    })
  });

  formModel: DayOfWeekItem[] = [
    {
      dayOfWeek: 'monday',
      position: 1,
      selected: false,
      times: []
    },
    {
      dayOfWeek: 'tuesday',
      position: 2,
      selected: false,
      times: []
    },
    {
      dayOfWeek: 'wednesday',
      position: 3,
      selected: false,
      times: []
    },
    {
      dayOfWeek: 'thursday',
      position: 4,
      selected: false,
      times: []
    },
    {
      dayOfWeek: 'friday',
      position: 5,
      selected: false,
      times: []
    },
    {
      dayOfWeek: 'saturday',
      position: 6,
      selected: false,
      times: []
    },
    {
      dayOfWeek: 'sunday',
      position: 7,
      selected: false,
      times: []
    },
  ];

  constructor(
    private _cd: ChangeDetectorRef
  ) {
    super();
  }

  get value() {
    return this.getValue();
  }

  /**
   * returns the first visible day item
   * used to determine when to show the applyToAllCheckbox
   */
  get firstVisibleDay() {
    return this.formModel.find(fm => fm.selected);
  }

  ngOnInit(): void {
    this.form.controls.shiftDays.valueChanges
      .pipe(
        takeUntil(this.disposer)
      ).subscribe((days: string[]) => {
        this.updateShiftTimes(days);
      });

    // Propagate the change
    this.form.valueChanges.pipe(
      startWith(this.form.value),
      takeUntil(this.disposer)
    ).subscribe(
      () => this.propagateChange(this.value)
    );

    setTimeout(() => this.form.updateValueAndValidity());
  }

  /**
   * Triggers when autocomplete panel is opened.
   * This scrolls the panel to the selected value.
   */
  autoCompleteOpened(currContext: string, currValue: string = null) {
    // wait 50ms for options to render
    const ac = (currContext === 'end') ? this.endTimeAutocomplete : this.startTimeAutocomplete;
    setTimeout(() => {
      const children = ac.panel?.nativeElement?.children || [];
      const nearestIndex = this.getNearestIndex(this.timeList, currValue);
      try {
        children[nearestIndex]?.scrollIntoView();
        ac._keyManager.setActiveItem(nearestIndex);
      } catch (e) {
        // In case the index is out of bounds
        console.error(e);
      }
    }, 50);
  }

  private getNearestIndex(options: any, value: string) {
    // Our dropdown contains only 15 minute intervals, but input can be any minute.
    // First check to see if the input is in our list (easy case)
    let nearestIndex = options.findIndex(option => option.value === value);
    if (nearestIndex === -1) {
      nearestIndex = options.findIndex(option => Number(option.value) > Number(value));
    }

    return nearestIndex;
  }

  onApplyShiftToAllClicked(shiftTime: any) {
    this.formModel
      .filter(t => t.selected)
      .forEach((di) => {
        // Skip any days which already have the shift
        if (!di.times.some(time => _isEqual(time, shiftTime))) {
          // Copy shift and put into the day
          di.times = [...di.times, _cloneDeep(shiftTime)]
            .filter(st => this.hasStartAndEnd(st));
        }
      });
    this.propagateChange();
  }

  endTimeChanged(selectedTime: string, shiftTime: any, dayOfWeek: DayOfWeek, trigger: MatAutocompleteTrigger) {
    if (selectedTime === 'next_day') {
      return this.nextDayClicked(dayOfWeek, shiftTime, trigger);
    }

    shiftTime.endTime = selectedTime;
    if (!this.isEndAfterStart(shiftTime.startTime, selectedTime)) {
      shiftTime.startTime = null;
    }
    this.propagateChange();
  }

  startTimeChanged(selectedTime: string, shiftTime: any) {
    shiftTime.startTime = selectedTime;

    if (!this.isEndAfterStart(selectedTime, shiftTime.endTime)) {
      shiftTime.endTime = null;
    }
    this.propagateChange();
  }

  /** Triggered when 'next day' pseudo-option is selected
   *  Adds the next day into the list if it doesn't already exist
   */
  nextDayClicked(dayOfWeek: DayOfWeek, shiftTime: StartEndTime, trigger: MatAutocompleteTrigger) {
    shiftTime.endTime = '23:59';
    if (trigger) {
      trigger.closePanel()
    }

    const nextDayName = this.getNextDay(dayOfWeek);
    const nextDayItem = this.formModel.find(d => d.dayOfWeek === nextDayName);
    const shiftDaysControl = this.form.get('shiftDays');

    // Check if there is a value for start and end times
    // already in the next day.
    // If there is not, then push a start time of midnight
    // into the next day.
    // For example, selecting 'Next Day' for the day of
    // monday will cause, a shift with a start time 12:00AM
    // on tuesday to be added
    const nextDayTimes = nextDayItem.times;
    if (isNilOrEmpty(nextDayTimes) || isNilOrEmpty(nextDayTimes[0].startTime)) {
      nextDayItem.times = [{
        startTime: '00:00',
        endTime: null
      }];

      // Select the end time input after we have done other updates
      setTimeout(() => {
        const identifier = `${nextDayName}-null`;
        const input = this.endTimeInputs
          .find(ti => ti.nativeElement.dataset.shiftInputDayTime === identifier);
        if (input) {
          input.nativeElement.focus();
        }
      }, 100)
    }

    shiftDaysControl.setValue(
      _uniq([...shiftDaysControl.value, nextDayName])
    );
  }

  addShiftClicked(dayItem: any) {
    dayItem.times = [...dayItem.times,
    {
      startTime: null,
      endTime: null
    }];
    this.propagateChange();
  }

  removeShiftClicked(dayItem: any, shiftTime) {
    if (dayItem.times.length === 1) {
      shiftTime.startTime = null;
      shiftTime.endTime = null;
    } else {
      dayItem.times = dayItem.times.filter(s => s !== shiftTime);
    }
    this.propagateChange();
  }

  /**
   * Returns true if the end time is after the start time
   *
   * @param startTime Start time  formatted hh:mm
   * @param endTime End time formatted hh:mm
   */
  isEndAfterStart(startTime: string, endTime: string) {
    if (isNilOrEmpty(startTime) || isNilOrEmpty(endTime)) {
      return false;
    }

    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);

    if (endHour > startHour) {
      return true;
    } else if (endHour === startHour) {
      return endMinute > startMinute;
    } else {
      return false;
    }
  }

  addShiftDisabled(endTime: string) {
    const disabled = endTime === '23:59' || isNilOrEmpty(endTime);
    return disabled;
  }

  hasStartOrEnd(shiftTime: { startTime?: string; endTime?: string }) {
    return !isNilOrEmpty(shiftTime.startTime) || !isNilOrEmpty(shiftTime.endTime);
  }

  hasStartAndEnd(shiftTime: { startTime?: string; endTime?: string }) {
    return !isNilOrEmpty(shiftTime) && !isNilOrEmpty(shiftTime.startTime) && !isNilOrEmpty(shiftTime.endTime);
  }

  /**
   * Given an array of shift times
   * returns `true` if any of the shifts
   * overlap
   *
   * @param times Shift times
   */
  doShiftsOverlap(times: [{ startTime?: string; endTime?: string }]) {
    // let hasOverlap = false;
    if (isNilOrEmpty(times)) {
      // Empty shifts have no overlap
      return false;
    }

    return doTimeIntervalsOverlap(times as { startTime: string; endTime: string }[]);
  }

  /**
   * Ensures that an end time is set if a start time is set
   *
   * @param times Shift times for day
   */
  missingEndTime(times: [{ startTime?: string; endTime?: string }]) {
    return times.some(t => !isNilOrEmpty(t.startTime) && isNilOrEmpty(t.endTime));
  }

  // ControlValueAccessor Implementation
  propagateChange: any = () => { };
  onTouched: any = () => { };
  onValidatorChange: any = () => { };

  registerOnChange(fn: any): void {
    this.propagateChange = () => fn(this.value);
  }
  registerOnTouched(fn: any): void {
    this.onTouched = () => fn(this.value);
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  validate(c: AbstractControl): { [key: string]: any } {
    const noShifts = !this.formModel.some(m => m.selected);

    const timesInvalid = this.formModel.some(d => {
      const times = d.times as any;
      return this.doShiftsOverlap(times) || this.missingEndTime(times);
    });

    if (noShifts || timesInvalid || this.form.get('timezone').invalid) {
      return { shift: true };
    }

    return null; // form is valid
  }

  // Incoming value will always be a shift time input array
  writeValue(obj: ShiftInfoFormModel): void {
    if (isNilOrEmpty(obj)) {
      this.form.reset();
    } else {
      const shiftTimes = _cloneDeep(obj?.shiftTimes || []);
      const days = _uniq(shiftTimes.map(st => st.dayOfWeek));
      this.formModel.forEach(item => {
        item.times = shiftTimes.filter(o => o.dayOfWeek === item.dayOfWeek);
      });
      this.form.patchValue({
        shiftDays: days,
        timezone: obj.timezone
      });
    }
    this.propagateChange();
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private getValue(): ShiftInfoFormModel {
    const timezone = this.form.get('timezone').value;
    const shiftTimes: ShiftTimeInputModel[] = this.formModel
      .filter(d => d.selected)
      .map(({ dayOfWeek, times }) => times.map(({ startTime, endTime }) => ({
        dayOfWeek, startTime, endTime
      })
      ))
      .reduce((a, b) => a.concat(b), []);

    return { shiftTimes, timezone };
  }

  private getNextDay(dayOfWeek: DayOfWeek) {
    const dayValues = this.dayList.map(d => d.value);
    const dayIndex = dayValues.findIndex(d => d === dayOfWeek);
    // Last day returns first day
    if (dayIndex === 6) {
      return dayValues[0];
    }
    // input is 'monday', returns 'tuesday'
    return dayValues[dayIndex + 1];
  }

  private updateShiftTimes(newDaysOfWeek: string[]) {
    _forEach(this.formModel, (v, k) => {
      v.selected = (newDaysOfWeek || []).includes(v.dayOfWeek);
      if (v.selected && isNilOrEmpty(v.times)) {
        v.times = [{
          startTime: null,
          endTime: null
        }];
      }
    });
  }

}
